/**
 * Section-model helpers.
 *
 * The email content model is an ordered array of typed sections (text | image | spacer).
 * Legacy rows stored `{ title?, content?, image? }`; `normalizeSections` upgrades those to
 * the typed shape on read, and `serializeSections` writes a clean typed array on save
 * (lazy migration — no destructive DB change).
 */

function toCells(image?: EmailImageT | EmailImageT[]): EmailImageT[] {
  if (!image) return []
  return Array.isArray(image) ? [...image] : [image]
}

export function isTextSection(section: EmailSectionT): boolean {
  return (section.type ?? (section.title || section.content ? 'text' : undefined)) === 'text'
}

export function isImageSection(section: EmailSectionT): boolean {
  return (section.type ?? (section.image ? 'image' : undefined)) === 'image'
}

export function isSpacerSection(section: EmailSectionT): boolean {
  return section.type === 'spacer'
}

/** Cells of an image band, tolerant of the legacy `image` field. */
export function sectionCells(section: EmailSectionT): EmailImageT[] {
  if (section.cells) return section.cells
  return toCells(section.image)
}

/**
 * Convert any (legacy or typed) section array into canonical typed sections.
 * Legacy `{title|content}` → a text section; legacy `{image}` (single or array, rendered
 * stacked today) → one image band per image (single cell each) to preserve the stacked
 * layout. Idempotent: already-typed sections pass through unchanged.
 */
export function normalizeSections(raw?: EmailSectionT[]): EmailSectionT[] {
  if (!raw?.length) return []

  const out: EmailSectionT[] = []

  for (const s of raw) {
    if (s.type === 'spacer') {
      out.push({ type: 'spacer', height: s.height ?? 24 })
      continue
    }

    if (s.type === 'text') {
      out.push({ type: 'text', heading: s.heading ?? s.title, body: s.body ?? s.content, style: s.style })
      continue
    }

    if (s.type === 'image') {
      out.push({ type: 'image', cells: s.cells ?? toCells(s.image) })
      continue
    }

    if (s.type === 'button') {
      out.push({ type: 'button', buttonText: s.buttonText ?? '', buttonUrl: s.buttonUrl ?? '', buttonStyle: s.buttonStyle })
      continue
    }

    if (s.type === 'divider') {
      out.push({ type: 'divider', dividerColor: s.dividerColor, dividerThickness: s.dividerThickness ?? 1 })
      continue
    }

    if (s.type === 'html') {
      const html: EmailSectionT = { type: 'html', html: s.html ?? '' }
      if (s.raw) html.raw = true
      out.push(html)
      continue
    }

    // Legacy section (no discriminator): split into a text block + image band(s).
    if (s.title || s.content) {
      out.push({ type: 'text', heading: s.title, body: s.content, style: s.style })
    }
    if (s.image) {
      for (const img of toCells(s.image)) {
        out.push({ type: 'image', cells: [img] })
      }
    }
  }

  return out
}

/** Strip legacy keys and emit a clean typed array ready to persist. */
export function serializeSections(sections: EmailSectionT[]): EmailSectionT[] {
  return normalizeSections(sections).map((s) => {
    if (s.type === 'text') {
      const out: EmailSectionT = { type: 'text', heading: s.heading, body: s.body }
      if (s.style) out.style = s.style
      return out
    }
    if (s.type === 'spacer') {
      return { type: 'spacer', height: s.height ?? 24 }
    }
    if (s.type === 'button') {
      const out: EmailSectionT = { type: 'button', buttonText: s.buttonText ?? '', buttonUrl: s.buttonUrl ?? '' }
      if (s.buttonStyle) out.buttonStyle = s.buttonStyle
      return out
    }
    if (s.type === 'divider') {
      return { type: 'divider', dividerColor: s.dividerColor, dividerThickness: s.dividerThickness ?? 1 }
    }
    if (s.type === 'html') {
      const out: EmailSectionT = { type: 'html', html: s.html ?? '' }
      if (s.raw) out.raw = true
      return out
    }
    return { type: 'image', cells: (s.cells ?? []).map((c) => ({ ...c })) }
  })
}

// Section factories used by the editor.
export function createTextSection(): EmailSectionT {
  return { type: 'text', heading: '', body: '', style: { align: 'center' } }
}

export function createImageSection(): EmailSectionT {
  return { type: 'image', cells: [{ src: '$SIZE_01.jpg' }] }
}

export function createSpacerSection(): EmailSectionT {
  return { type: 'spacer', height: 24 }
}

export function createButtonSection(): EmailSectionT {
  return { type: 'button', buttonText: 'Shop now', buttonUrl: '', buttonStyle: { align: 'center', background: '#151e6d', color: '#ffffff', radius: 6 } }
}

export function createDividerSection(): EmailSectionT {
  return { type: 'divider', dividerColor: '#dddddd', dividerThickness: 1 }
}

/**
 * Raw-HTML mode is persisted (migration-free) as a single html section flagged `raw`,
 * stored in the existing `sections` jsonb. These helpers detect/extract it so the builder
 * and editor can treat such an email as "whole body is raw HTML".
 */
export function isRawHtmlEmail(sections?: EmailSectionT[]): boolean {
  return !!sections && sections.length === 1 && sections[0]?.type === 'html' && !!sections[0]?.raw
}

export function rawHtmlFromSections(sections?: EmailSectionT[]): string {
  return isRawHtmlEmail(sections) ? (sections![0]!.html || '') : ''
}

/** Encode raw-HTML mode back into the sections array for persistence. */
export function rawHtmlToSections(rawHtml: string): EmailSectionT[] {
  return [{ type: 'html', html: rawHtml || '', raw: true }]
}

export function createHtmlSection(): EmailSectionT {
  return {
    type: 'html',
    html: '<!-- Custom HTML block. Inline styles survive best; <style> is inlined on export. -->\n<p style="margin:0;font-family:Arial,sans-serif;font-size:15px;color:#333;">\n  Edit this HTML…\n</p>',
  }
}
